import React from 'react';
import { Card } from '../components/ui';

const TermsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
    <div className="max-w-4xl mx-auto space-y-6">
      <Card className="p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms and Conditions</h1>
            <p className="text-lg text-gray-600 font-medium">AVI Platform by Athletiverse, Inc.</p>
            <p className="text-sm text-gray-500 mt-2">Effective Date: August 31, 2025</p>
            <p className="text-sm text-gray-500">Last Updated: May 14, 2026</p>
          </div>
          
          <div className="prose prose-slate max-w-none text-sm">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">1. ACCEPTANCE OF TERMS</h2>
              <p className="text-gray-700 mb-4">
                These Terms and Conditions ("Terms") constitute a legally binding agreement between you ("Customer," "you," or "your") and Athletiverse, Inc., a Delaware corporation ("Athletiverse," "AVI," "we," "us," or "our"), concerning your access to and use of the AVI platform and services (the "Platform" or "Services").
              </p>
              <p className="text-gray-700 mb-4">
                By accessing or using the Platform, creating an account, or clicking "I Agree," you accept and agree to be bound by these Terms. If you do not agree to these Terms, you must not access or use the Platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">2. PLATFORM DESCRIPTION</h2>
              <p className="text-gray-700 mb-4">AVI is a marketing analytics platform that provides:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Social media performance analytics across Instagram, LinkedIn, Twitter/X, YouTube, and newsletter platforms</li>
                <li>Earned Media Value (EMV) calculations and tracking</li>
                <li>AI-powered post performance insights and analysis</li>
                <li>Competitive benchmarking and analytics</li>
                <li>Click-through tracking and attribution</li>
                <li>Multi-channel performance aggregation</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">3. ACCOUNT REGISTRATION AND MANAGEMENT</h2>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">3.1 Account Creation</h3>
              <p className="text-gray-700 mb-4">
                To use the Platform, you must create an account providing accurate, current, and complete information. You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account.
              </p>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3">3.2 Multi-Tenant Structure</h3>
              <p className="text-gray-700 mb-4">
                Each Customer account operates as a separate tenant with isolated data. Accounts may have multiple authorized users with different permission levels (administrators and general users).
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">3.3 Account Security</h3>
              <p className="text-gray-700 mb-2">You must:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Choose a strong password meeting our security requirements</li>
                <li>Immediately notify us of any unauthorized use of your account</li>
                <li>Accept responsibility for all activities occurring under your account</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">4. SUBSCRIPTIONS AND BILLING</h2>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3">4.1 Subscription Tiers</h3>
              <p className="text-gray-700 mb-4">
                The Platform offers various subscription tiers with different features and usage limits. Current tier details and pricing are available on our website or through your account dashboard.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">4.2 Payment Terms</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>All subscriptions are billed in advance on a monthly or annual basis</li>
                <li>Payment is processed automatically via Stripe using your provided payment method</li>
                <li>You authorize us to charge your payment method for all fees associated with your subscription</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">4.3 Upgrades and Downgrades</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Upgrades take effect immediately with prorated billing</li>
                <li>Downgrades take effect at the next billing cycle</li>
                <li>No refunds are provided for downgrading during a billing period</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">4.4 Refunds</h3>
              <p className="text-gray-700 mb-4">
                Refund requests are evaluated on a case-by-case basis at our sole discretion. To request a refund, contact info@athletiverse.io with your account details and reason for the request.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">4.5 Taxes</h3>
              <p className="text-gray-700 mb-4">
                All fees are exclusive of applicable taxes. You are responsible for all taxes associated with your use of the Platform, except for taxes based on our net income.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">5. ACCEPTABLE USE</h2>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3">5.1 Permitted Use</h3>
              <p className="text-gray-700 mb-4">
                You may use the Platform solely for lawful business purposes in accordance with these Terms.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">5.2 Prohibited Activities</h3>
              <p className="text-gray-700 mb-2">You shall not:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe upon intellectual property rights</li>
                <li>Transmit malicious code or interfere with the Platform's operation</li>
                <li>Attempt to gain unauthorized access to any systems or data</li>
                <li>Scrape, harvest, or collect data through unauthorized means</li>
                <li>Resell or redistribute Platform services without written permission</li>
                <li>Use the Platform to harm, harass, or defame others</li>
                <li>Misrepresent your identity or affiliation</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">6. INTELLECTUAL PROPERTY</h2>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3">6.1 Platform Ownership</h3>
              <p className="text-gray-700 mb-2">Athletiverse owns all rights, title, and interest in the Platform, including:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>The AVI platform software and technology</li>
                <li>EMV calculation methodologies and algorithms</li>
                <li>AI models and analytical frameworks</li>
                <li>Platform-generated metrics and insights</li>
                <li>User interface designs and features</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">6.2 Customer Content</h3>
              <p className="text-gray-700 mb-4">
                You retain ownership of your social media content and data uploaded to the Platform ("Customer Content"). By using the Platform, you grant Athletiverse a worldwide, non-exclusive license to process, analyze, and display your Customer Content solely for providing the Services.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">6.3 Feedback</h3>
              <p className="text-gray-700 mb-4">
                Any feedback, suggestions, or improvements you provide regarding the Platform become the property of Athletiverse and may be used without compensation or attribution.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">7. DATA AND PRIVACY</h2>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3">7.1 Data Processing</h3>
              <p className="text-gray-700 mb-4">
                We process your data in accordance with our Privacy Statement, which is incorporated by reference into these Terms.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">7.2 Data Sharing</h3>
              <p className="text-gray-700 mb-4">
                We may share aggregated, anonymized data with partners for service improvement purposes. We do not sell your personal or business data to third parties.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">7.3 Data Retention</h3>
              <p className="text-gray-700 mb-4">
                We retain your data for as long as your account is active or as needed to provide services. Upon account deletion or written request to info@athletiverse.io, we will remove your data within 30 days, except where retention is required by law.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">8. THIRD-PARTY INTEGRATIONS</h2>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">8.1 Social Media Platforms</h3>
              <p className="text-gray-700 mb-4">
                The Platform integrates with third-party services including Instagram, LinkedIn, Twitter/X, YouTube, and BeeHiiv. Your use of these integrations is subject to the respective third-party terms and conditions.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">8.2 Instagram and Facebook (Meta) Integration</h3>
              <p className="text-gray-700 mb-4">
                The Platform's Enhanced API feature connects to your Instagram Professional account via the Meta Graph API. By enabling this feature, you authorize Athletiverse to access your Instagram Business Account data, including post metrics and audience analytics, as described in our Privacy Statement. This access is governed by Meta's <a href="https://www.facebook.com/terms.php" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Terms of Service</a> and <a href="https://developers.facebook.com/terms/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Platform Terms</a>. Athletiverse complies with Meta's Platform Terms and Developer Policies in its use of this integration.
              </p>
              <p className="text-gray-700 mb-4">
                You may revoke this access at any time via Settings → Social Accounts within the Platform, or directly through your Facebook account settings under Apps and Websites. Upon disconnection, OAuth credentials will be deleted within 30 days in accordance with our Privacy Statement.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">8.3 Third-Party Limitations</h3>
              <p className="text-gray-700 mb-2">We are not responsible for:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Changes to third-party APIs or services</li>
                <li>Downtime or failures of third-party platforms</li>
                <li>Data accuracy from third-party sources</li>
                <li>Actions taken by third-party platforms affecting your accounts</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">9. SERVICE AVAILABILITY</h2>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3">9.1 Uptime Commitment</h3>
              <p className="text-gray-700 mb-4">
                We strive to maintain 99.99% Platform availability but do not guarantee uninterrupted service. Availability excludes scheduled maintenance and third-party service failures.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">9.2 Modifications</h3>
              <p className="text-gray-700 mb-4">
                We reserve the right to modify, suspend, or discontinue any aspect of the Platform with reasonable notice when practicable.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">10. DISCLAIMER OF WARRANTIES</h2>
              <div className="bg-gray-100 p-4 rounded-lg mb-4">
                <p className="text-gray-700 font-medium">
                  THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR ACCURACY OF DATA.
                </p>
              </div>
              <p className="text-gray-700 mb-2 font-medium">WE DO NOT WARRANT THAT:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>THE PLATFORM WILL MEET ALL YOUR REQUIREMENTS</li>
                <li>THE PLATFORM WILL BE ERROR-FREE OR UNINTERRUPTED</li>
                <li>EMV CALCULATIONS OR AI INSIGHTS WILL BE 100% ACCURATE</li>
                <li>THIRD-PARTY INTEGRATIONS WILL ALWAYS FUNCTION</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">11. LIMITATION OF LIABILITY</h2>
              <div className="bg-gray-100 p-4 rounded-lg mb-4">
                <p className="text-gray-700 font-medium mb-2">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, ATHLETIVERSE SHALL NOT BE LIABLE FOR:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-2">
                  <li>ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
                  <li>LOST PROFITS, REVENUE, DATA, OR BUSINESS OPPORTUNITIES</li>
                  <li>DAMAGES EXCEEDING THE FEES PAID BY YOU IN THE TWELVE MONTHS PRECEDING THE CLAIM</li>
                </ul>
                <p className="text-gray-700 font-medium">
                  THIS LIMITATION APPLIES REGARDLESS OF THE THEORY OF LIABILITY AND WHETHER WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">12. INDEMNIFICATION</h2>
              <p className="text-gray-700 mb-2">
                You agree to indemnify, defend, and hold harmless Athletiverse and its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including reasonable attorneys' fees) arising from:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Your violation of these Terms</li>
                <li>Your use of the Platform</li>
                <li>Your Customer Content</li>
                <li>Your violation of any third-party rights</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">13. DISPUTE RESOLUTION</h2>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3">13.1 Arbitration Agreement</h3>
              <p className="text-gray-700 mb-4">
                Any dispute arising from these Terms or your use of the Platform shall be resolved through binding arbitration in accordance with the American Arbitration Association's Commercial Arbitration Rules.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">13.2 Arbitration Procedures</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Arbitration shall be conducted in Delaware</li>
                <li>Each party bears its own costs unless the arbitrator determines otherwise</li>
                <li>The arbitrator's decision is final and binding</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">13.3 Exceptions</h3>
              <p className="text-gray-700 mb-4">
                Notwithstanding the above, either party may seek injunctive relief in court for intellectual property violations or breach of confidentiality.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">14. GENERAL PROVISIONS</h2>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3">14.1 Governing Law</h3>
              <p className="text-gray-700 mb-4">
                These Terms are governed by Delaware law, excluding conflict of law principles.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">14.2 Entire Agreement</h3>
              <p className="text-gray-700 mb-4">
                These Terms and the Privacy Statement constitute the entire agreement between you and Athletiverse regarding the Platform.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">14.3 Severability</h3>
              <p className="text-gray-700 mb-4">
                If any provision is found unenforceable, the remaining provisions continue in effect.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">14.4 Assignment</h3>
              <p className="text-gray-700 mb-4">
                You may not assign these Terms without our written consent. We may assign our rights and obligations without restriction.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">14.5 Notices</h3>
              <p className="text-gray-700 mb-2">Legal notices must be sent to:</p>
              <div className="bg-gray-50 p-3 rounded mb-4">
                <p className="text-gray-700">
                  Athletiverse, Inc.<br/>
                  8635 W. Sahara Ave #528<br/>
                  Las Vegas, NV 89117<br/>
                  info@athletiverse.io
                </p>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">14.6 Changes to Terms</h3>
              <p className="text-gray-700 mb-4">
                We may update these Terms by posting revised versions on the Platform. Continued use after changes indicates acceptance.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">15. CONTACT INFORMATION</h2>
              <p className="text-gray-700 mb-2">For questions about these Terms, contact us at:</p>
              <ul className="list-disc pl-6 text-gray-700">
                <li>Email: info@athletiverse.io</li>
                <li>Address: 8635 W. Sahara Ave #528, Las Vegas, NV 89117</li>
              </ul>
            </section>
          </div>
        </Card>
    </div>
    </div>
  );
};

export default TermsPage;